
'use client';

import type { Table } from '@tanstack/react-table';
import type { ColumnConfig } from '../types';
import { calculateSummary } from '../utils/exportUtils';

interface SummaryRowProps {
  table: Table<any>;
  columns: ColumnConfig[];
  data: any[];
}

export const SummaryRow: React.FC<SummaryRowProps> = ({ table, columns, data }) => {
  return (
    <tfoot className="bg-gray-50 dark:bg-gray-800 border-t-2 border-gray-200 dark:border-gray-600">
      <tr>
        {/* Selection column */}
        <td className="px-6 py-3 text-sm font-bold tracking-wide uppercase text-indigo-700 bg-indigo-50/50 dark:bg-indigo-900/20 dark:text-indigo-300">
          Total
        </td>
        
        {table.getVisibleLeafColumns().slice(1).map((column) => {
          const columnConfig = columns.find(col => 
            (col.field || col.columnName) === column.id
          );
          
          if (!columnConfig?.summaryType) {
            return <td key={column.id} className="px-6 py-3"></td>;
          }

          const summaryValue = calculateSummary(
            data, 
            columnConfig.field!, 
            columnConfig.summaryType
          );

          let displayValue: string | number = summaryValue;

          // Format based on column type
          if (columnConfig.columnType === 'currency') {
            const symbol = columnConfig.displayFormat === 'INR' ? '₹' : '$';
            const locale = columnConfig.displayFormat === 'INR' ? 'en-IN' : 'en-US';
            displayValue = `${symbol}${summaryValue.toLocaleString(locale)}`;
          } else if (columnConfig.columnType === 'number') {
            // Use Indian locale for number formatting (no currency symbol for plain numbers)
            displayValue = summaryValue.toLocaleString('en-IN');
          } else if (columnConfig.summaryType === 'avg') {
            displayValue = summaryValue.toFixed(2);
          }

          const alignmentClass = columnConfig.alignment === 'center' ? 'text-center' :
                                columnConfig.alignment === 'right' ? 'text-right' : 'text-left';

          return (
            <td key={column.id} className={`px-6 py-3 text-sm font-semibold text-gray-900 dark:text-gray-100 ${alignmentClass}`}>
              <div className="flex flex-col">
                <span>{displayValue}</span>
                {columnConfig.summaryLabel && (
                  <span className="text-xs text-gray-500 dark:text-gray-400">{columnConfig.summaryLabel}</span>
                )}
              </div>
            </td>
          );
        })}
      </tr>
    </tfoot>
  );
};
